import React from 'react';
import clsx from 'clsx';
import { observer } from 'mobx-react-lite';
import { FocusHandler, useFocusable } from '@noriginmedia/norigin-spatial-navigation';
import { IS_BROWSER } from '@smart-tv/utils/is-browser';

import ProgressBar from '@smart-tv/components/progress-bar/progress-bar';
import Typography from '@smart-tv/components/typography';
import PlayIcon from '@smart-tv/components/icons/play';
import { formatDuration } from '@smart-tv/utils/formatter/format-duration';

import styles from '../episode-selector.module.scss';
import { getEpisodeCardFocusKey } from '../sheet-helpers';
import { rootStore } from '../_stores/store-root';
import { renderAirtime } from './sheet-description-info-airtime';
import { EpisodeExtended } from '../types';
import { PadlockIcon } from '@smart-tv/components/icons/padlock';

export interface EpisodeCardProps {
  episode: EpisodeExtended;
  episodeIndex: number;
  onFocus: (episode: EpisodeExtended) => void;
  onMouseEnterEpisode?: (episode: EpisodeExtended) => void;
  onMouseLeaveEpisode?: () => void;
  onEnterPress?: () => void;
  onUpArrow: () => void;
  onClick?: () => void;
  listRef: React.RefObject<HTMLDivElement>;
  isFutureEpisodes?: boolean;
  isSelectorFocused?: boolean;
}

const EpisodeCard: React.FC<EpisodeCardProps> = observer(
  ({
    episode,
    episodeIndex,
    onFocus,
    onMouseEnterEpisode,
    onMouseLeaveEpisode,
    onEnterPress,
    onUpArrow,
    onClick,
    isFutureEpisodes,
    isSelectorFocused = false,
    listRef
  }) => {
    const { focusedEpisode } = rootStore.episodesStore;
    const selected =
      focusedEpisode.episodeId === episode.episodeId &&
      focusedEpisode.isNextEventforSeries === episode.isNextEventforSeries;

    const handleNavFocus = React.useCallback<FocusHandler>(
      ({ node }) => {
        onFocus(episode);
        if (listRef.current) {
          // Use the node's own offsetLeft (stable) rather than the scroll-relative x from the layout,
          // which would compound drift across successive focus moves.
          listRef.current.scrollLeft = Math.max(
            0,
            node.offsetLeft - listRef.current.clientWidth / 3
          );
        }
      },
      [episode, listRef, onFocus]
    );

    const handleArrowPress = React.useCallback(
      (direction: string) => {
        if (direction === 'up') {
          onUpArrow();
          return false;
        }

        if (direction === 'left' && episodeIndex === 0) {
          return false;
        }

        return true;
      },
      [episodeIndex, onUpArrow]
    );

    const { ref, focused } = useFocusable({
      focusKey: getEpisodeCardFocusKey(episode.episodeId, episode.isNextEventforSeries),
      onEnterPress,
      onFocus: handleNavFocus,
      onArrowPress: handleArrowPress
    });

    const imageUrl = React.useMemo(() => {
      const cover = episode.isNextEventforSeries ? episode.channelIcon : episode.cover;
      if (!cover) return '';
      return cover.includes('w=1920&h=1080')
        ? cover.replace('w=1920&h=1080', 'w=290&h=163')
        : cover;
    }, [episode.cover, episode.channelIcon, episode.isNextEventforSeries]);

    const percentageWatched = React.useMemo(() => {
      if (episode.providers?.[0]?.length) {
        return (episode.providers[0].secondsWatched / episode.providers[0].length) * 100;
      }
      return 0;
    }, [episode.providers]);

    const metaText = React.useMemo(() => {
      const parts: string[] = [];
      if (episode.providers?.[0]?.length) parts.push(formatDuration(episode.providers[0].length));
      if (episode.isNextEventforSeries) {
        const version = episode.providers?.[0]?.versions?.[0];
        const airTime =
          version?.readableAirTimeOrRecordingTime ??
          episode.readableAirTimeOrRecordingTime ??
          (version?.airTimeStart
            ? renderAirtime(new Date(version.airTimeStart * 1000), undefined, true)
            : undefined);
        if (airTime) parts.push(airTime);
      }
      return parts.join(' · ');
    }, [episode.isNextEventforSeries, episode.providers, episode.readableAirTimeOrRecordingTime]);

    return (
      <div
        ref={ref}
        data-episode-index={episodeIndex}
        className={clsx(styles['episode-card'], {
          [styles['episode-card--focused']]: focused,
          [styles['episode-card--selected']]: selected
        })}
        onMouseEnter={
          IS_BROWSER
            ? () => {
                onFocus(episode);
                onMouseEnterEpisode?.(episode);
              }
            : undefined
        }
        onMouseLeave={IS_BROWSER ? () => onMouseLeaveEpisode?.() : undefined}
        onClick={() => {
          onFocus(episode);
          onClick?.();
        }}
      >
        <div className={styles['episode-card__image-wrapper']}>
          <div
            className={clsx(styles['episode-card__image'], {
              [styles['episode-card__image--no-access']]:
                !episode.hasProviderAccess && !isFutureEpisodes
            })}
            style={{ ['--image-src' as string]: `url(${imageUrl})` }}
          />
          {!isFutureEpisodes && episode.hasProviderAccess && (
            <div className={styles['episode-card__play-icon']}>
              <PlayIcon size="xl" />
            </div>
          )}
          {percentageWatched > 0 && (
            <ProgressBar
              className={styles['episode-card__progress-bar']}
              value={percentageWatched}
            />
          )}
          {!episode.hasProviderAccess && !isFutureEpisodes && (
            <PadlockIcon
              size="xl"
              className={clsx(styles['episode-card__padlock-icon'], {
                [styles['episode-card__padlock-icon--bottom']]: isSelectorFocused || IS_BROWSER,
                [styles['episode-card__padlock-icon--top']]: !isSelectorFocused && !IS_BROWSER
              })}
            />
          )}
        </div>
        <div className={styles['episode-card__detail']}>
          {metaText && (
            <Typography variant="caption2" className={styles['episode-card__meta']}>
              {metaText}
            </Typography>
          )}

          <Typography variant="caption" className={styles['episode-card__title']}>
            {episode.episodeNumber > 0
              ? `${episode.episodeNumber}. ${episode.episodeTitle}`
              : episode.episodeTitle}
          </Typography>
        </div>
      </div>
    );
  }
);

export default EpisodeCard;
